import { Component, DestroyRef, inject, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Store, select } from '@ngrx/store';
import { cloneDeep, includes, uniq } from 'lodash-es';
import { Observable } from 'rxjs';
import { filter } from 'rxjs/operators';

import { IFrameworkConfigSettings } from '../fw/fw-config/interfaces/i-framework-config-settings';
import { initialMenuItems } from './app.menu';

import * as fromAuth from '../auth/store/index';
import { getAllMyWidgets } from '../auth/store/index';
import { FrameworkConfigService } from '../fw/fw-config/services/framework-config.service';
import { MenuService } from '../fw/fw-menus/services/menu.service';
import { DuplicateInstanceService, GeolocationService, UserSessionEventService } from '../shared/services';
import { HomLocalStorageChangeService } from '../shared/services/hom-local-storage-change.service';
import { IWidgetManager } from './portals/portal/interfaces';
import { RouteToCrmService } from './route-to-crm/route-to-crm.service';
import * as fromRoot from './store/reducers/index';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { environment } from '../environments/environment';

/**
 * App Component is the starting point for all other components in our application
 */
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  authenticated$: Observable<boolean>;
  hasDashItems: boolean;
  private destroyRef = inject(DestroyRef);

  constructor(
    public router: Router,
    public frameworkConfigService: FrameworkConfigService,
    public menuService: MenuService,
    public rootStore: Store<fromRoot.IState>,
    public duplicateInstanceService: DuplicateInstanceService,
    public userSessionEventService: UserSessionEventService,
    public geolocationService: GeolocationService,
    public routeToCrmService: RouteToCrmService,
    public homLocalStorageChangeService: HomLocalStorageChangeService
  ) {}

  ngOnInit() {
    this.geolocationService.init();
    if (!environment.allowMultipleInstances) {
      this.duplicateInstanceService.init();
    }
    this.routeToCrmService.init();
    this.homLocalStorageChangeService.init();
    this.authenticated$ = this.rootStore.pipe(select(fromAuth.isAuthenticated));

    const config: IFrameworkConfigSettings = {
      appIcons: [
        {
          name: 'userSettings',
          icon: 'fal fa-cog',
          alt: 'My Settings',
          link: 'userSettings',
          selector: 'user-settings',
          modal: true,
        },
        {
          name: 'userRecents',
          icon: 'fal fa-bookmark',
          alt: 'My Bookmarks',
          link: 'userRecents',
          selector: 'user-recents',
          modal: false,
        },
        {
          name: 'userJobs',
          icon: 'fal fa-atom-alt',
          alt: 'My Jobs',
          link: 'userJobs',
          selector: 'user-jobs',
          modal: true,
        },
        {
          name: 'pendingChanges',
          icon: 'fal fa-inbox',
          alt: 'Pending Changes',
          link: 'pendingChanges',
          selector: 'user-pending-changes',
          modal: true,
        },
        {
          name: 'userNotifications',
          icon: 'fal fa-paper-plane',
          alt: 'My Notifications',
          link: 'userNotifications',
          selector: 'user-notifications',
          modal: true,
        },
      ],
      showStatusBarBreakpoint: 800,
    };

    this.frameworkConfigService.configure(config);

    this.authenticated$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((authenticated) => {
      if (authenticated) {
        if (!this.menuService.items) {
          this.setMenu();
        }
        this.userSessionEventService.init();
      }
    });
  }

  setMenu(): void {
    this.rootStore
      .pipe(
        select(getAllMyWidgets),
        filter((data: IWidgetManager[]) => data && data.length > 0),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((data: IWidgetManager[]) => {
        const availPortalIds: number[] = uniq(data.map((x) => x.managerPortal_managerPortalId));
        const menuItems = cloneDeep(initialMenuItems);
        this.menuService.items = menuItems.filter((x) => includes(availPortalIds, x.portalId));
      });
  }

  public ngOnDestroy(): void {
    // No manual unsubscribe needed when using takeUntilDestroyed
  }
}
